import isNil from 'lodash/isNil';
import { useState, useEffect, useMemo, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';

import { QueryKey } from '@ge/models/constants';
import { Config } from '@ge/shared/data-hooks';

import {
  Blades,
  FileConstants,
  InspectionStages,
  InspectionStage,
  StageStatus,
} from '../models/constants';
import * as appService from '../services/app';
import { droneSpreadsheetUpload } from '../services/downloadFiles';
import * as filesService from '../services/files';
import * as inspectionService from '../services/inspections';
import * as reportService from '../services/report';
import { isSabre, isBurst, isPinwheel, isInternal, isInternalGEBlade, isDrone } from '../util';

// going with what was already in the app, though sounds like it is closer to 15-minutes on the backend
const DEFAULT_SIGNED_URL_EXPIRATION = 60 * 60 * 12;
const INSPECTION_DATA_REFRESH_INTERVAL = 1000 * 60;
// const INSPECTION_STAGE_STATUS_REFRESH_INTERVAL = 1000 * 1;

const getS3Filename = (s3, filename) => `${s3}/input/${filename}`;

// get signed urls for img src attribute
// can revisit if preloading signed urls with an expiration is a good idea
export const useSignedUrlMap = ({ jobId, filenames }) => {
  const { data, error, isLoading } = useQuery(
    [QueryKey.INSPECTIONS_SIGNED_URLS, filenames],
    async () => {
      const data = {
        expires: DEFAULT_SIGNED_URL_EXPIRATION,
        file: filenames.map((filename) => filename.replace(/,/g, FileConstants.COMMA)).join(','),
        type: 'get',
        jobId: jobId,
      };

      const { signedUrls } = await filesService.getPresignedUrl(data);

      const signedUrlMap = signedUrls?.reduce(
        (_map, { file_name, url }) => _map.set(file_name, url),
        new Map(),
      );

      return {
        signedUrls: signedUrls,
        signedUrlMap: signedUrlMap,
      };
    },
    {
      ...Config.EXECUTE_ONCE,
      enabled: Boolean(filenames?.length),
      refetchInterval: Config.REFRESH.signedUrl,
    },
  );

  return { data, error, isLoading };
};

export const useStageStatus = ({ stage, status }) => {
  const [stageStatus, setStageStatus] = useState({
    upload: StageStatus.NA,
    execution: StageStatus.NA,
    post_process: StageStatus.NA,
    report_generation: StageStatus.NA,
  });

  useEffect(() => {
    const calculateStageStatus = (stage, status) => {
      if (stage === InspectionStage.UPLOAD) {
        setStageStatus((prev) => ({
          ...prev,
          upload: status,
        }));
      }
      if (stage === InspectionStage.EXECUTION) {
        setStageStatus((prev) => ({
          ...prev,
          upload: StageStatus.COMPLETE,
          execution: status,
        }));
      }
      if (stage === InspectionStage.POSTPROCESS) {
        setStageStatus((prev) => ({
          ...prev,
          upload: StageStatus.COMPLETE,
          execution: StageStatus.COMPLETE,
          post_process: status,
        }));
      }
      if (stage === InspectionStage.REPORT) {
        setStageStatus(() => ({
          upload: StageStatus.COMPLETE,
          execution: StageStatus.COMPLETE,
          post_process: StageStatus.COMPLETE,
          report_generation: status,
        }));
      }
    };

    return calculateStageStatus(stage, status);
  }, [stage, status]);

  return stageStatus;
};

// TODO: look into whether we still need this or can we pull the current image info from the images fetch?
// also look into why this keeps refetching with the same query key instead of using cached response
export const useImage = ({ imageId, staleTime = 10 * 60 * 1000 }) => {
  const { data = null, ...queryRest } = useQuery(
    ['useImage', +imageId],
    async () => {
      if (isNil(imageId)) {
        return null;
      }
      const { result } = await inspectionService.getImage(imageId, {});

      return result.data;
    },
    {
      ...Config.EXECUTE_ONCE,
      enabled: Boolean(imageId),
      staleTime,
    },
  );

  return {
    data,
    ...queryRest,
  };
};

export const useImages = ({ jobId, staleTime = 0 }) => {
  const { data = [], ...queryRest } = useQuery(
    ['useImages', +jobId],
    async () => {
      const { result } = await inspectionService.getImages(jobId, {});

      const { data } = result;

      return data.sort((a, b) => (a?.attributes.blade_index >= b?.attributes.blade_index ? 1 : -1));
    },
    {
      ...Config.EXECUTE_ONCE,
      enabled: Boolean(jobId),
      staleTime,
    },
  );

  const filenames = data?.map(({ attributes: { imgname } }) => imgname);

  const { data: result } = useSignedUrlMap({ jobId, filenames });

  const _data = useMemo(() => {
    if (!(data && result?.signedUrlMap)) {
      return undefined;
    }

    return data?.map((image) => ({
      ...image,
      annotations: result?.signedUrls.filter(
        (imageUrl) => imageUrl?.file_name === image.attributes.imgname,
      ),
      attributes: {
        ...image.attributes,
        src: result?.signedUrlMap.get(image.attributes.imgname),
      },
    }));
  }, [data, result?.signedUrlMap, result?.signedUrls]);

  return {
    data: _data,
    ...queryRest,
  };
};

export const useVideos = ({ jobId, staleTime = 10 * 60 * 1000, ...paramRest }) => {
  const { data = [], ...queryRest } = useQuery(
    ['useVideos', +jobId],
    async () => {
      const { results } = await inspectionService.getVideos(jobId, {});

      return results.data;
    },
    {
      ...Config.EXECUTE_ONCE,
      ...paramRest,
      enabled: Boolean(jobId),
      staleTime,
    },
  );

  const { data: inspection } = useInspection({ jobId, staleTime });

  const { s3 } = inspection ?? {};

  const filenames = s3
    ? data?.map(({ attributes: { videoname } }) => getS3Filename(s3, videoname))
    : undefined;

  const { data: result } = useSignedUrlMap({ jobId, filenames });

  const _data = useMemo(() => {
    if (!(data && s3 && result?.signedUrlMap)) {
      return [];
    }

    return data?.map((video) => ({
      ...video,
      attributes: {
        ...video.attributes,
        src: result?.signedUrlMap.get(getS3Filename(s3, video.attributes.videoname)),
      },
    }));
  }, [data, s3, result?.signedUrlMap]);

  return {
    data: _data,
    ...queryRest,
  };
};

export const useVideo = ({ jobId, videoId, staleTime = 10 * 60 * 1000 }) => {
  const [video, setVideo] = useState(null);
  const { data: videos = [] } = useVideos({ jobId, staleTime });
  useEffect(() => {
    const _video = videos.find((v) => v.id === videoId) || null;
    setVideo(_video);
  }, [videos, videoId]);
  return video;
};

export const useInspection = ({ jobId }) => {
  const { data = null, ...queryRest } = useQuery(
    ['useInspection', +jobId],
    async () => {
      if (jobId === null || jobId == 0) return null;
      const { result } = await inspectionService.getJob(jobId, {});
      return result.data.attributes;
    },
    {
      ...Config.EXECUTE_ONCE,
      staleTime: 0,
      cacheTime: 0,
    },
  );
  const _data = useMemo(() => data, [data]);
  return {
    data: _data,
    ...queryRest,
  };
};

export const useInspections = ({ staleTime = 10 * 30 * 1000 }) => {
  const {
    data = [],
    error,
    isLoading,
  } = useQuery(
    'useInspections',
    async () => {
      const { result } = await inspectionService.getAllJobs(10000, {});
      return result.data;
    },
    {
      staleTime,
      refetchInterval: INSPECTION_DATA_REFRESH_INTERVAL,
    },
  );
  return {
    data,
    error,
    isLoading,
  };
};

// does stale time have to be this low?
export const useAnnotationsByImageId = ({ imageId, staleTime = 500 }) => {
  const { data = [], ...queryRest } = useQuery(
    ['useAnnotationsByImageId', +imageId],
    async () => {
      const { result } = await inspectionService.getAnnotations(imageId, {});

      return result.data;
    },
    {
      ...Config.EXECUTE_ONCE,
      enabled: Boolean(imageId),
      staleTime,
    },
  );
  const _data = useMemo(() => data ?? [], [data]);
  return {
    data: _data,
    ...queryRest,
  };
};

export const useAnnotationById = ({ imageId = null, annotationId = null }) => {
  const [annotation, setAnnotation] = useState(null);
  const { data: annotations, refetch } = useAnnotationsByImageId({ imageId });
  useEffect(() => {
    const _annotation = annotations.find((anno) => anno.id === annotationId);
    setAnnotation(_annotation);
  }, [annotations, annotationId]);
  return { data: annotation, getAnnotationsByImageId: refetch };
};

// export const useJobExecution = ({ jobId, staleTime = 30 * 1000 }) => {
//   const { data = null, error, isLoading } = useQuery(
//     ['useJobExecution', jobId],
//     async () => {
//       const { result } = await inspectionService.getJobExecution(jobId, {});

//       return result.data;
//     },
//     {
//       ...Config.EXECUTE_ONCE,
//       enabled: Boolean(jobId),
//       staleTime,
//     },
//   );
//   return {
//     data,
//     error,
//     isLoading,
//   };
// };

export const useStructuredImagesByBlade = ({ jobId, videoId, selectedTools }) => {
  const initialStruct = useMemo(
    () => ({
      [Blades.ONE]: [],
      [Blades.TWO]: [],
      [Blades.THREE]: [],
      [Blades.ONE_UNKNOWN]: [],
      [Blades.TWO_UNKNOWN]: [],
      [Blades.THREE_UNKNOWN]: [],
      bladeMap: {
        [Blades.ONE]: 'blade1',
        [Blades.TWO]: 'blade2',
        [Blades.THREE]: 'blade3',
        [Blades.ONE_UNKNOWN]: 'unknown',
        [Blades.TWO_UNKNOWN]: 'unknown',
        [Blades.THREE_UNKNOWN]: 'unknown',
      },
      imagesViewed: {
        [Blades.ONE]: 0,
        [Blades.TWO]: 0,
        [Blades.THREE]: 0,
        [Blades.ONE_UNKNOWN]: 0,
        [Blades.TWO_UNKNOWN]: 0,
        [Blades.THREE_UNKNOWN]: 0,
      },
    }),
    [],
  );
  const { data: inspection = null } = useInspection({ jobId });
  const { data: allImages = [] } = useImages({ jobId });
  const { data: allVideos = [] } = useVideos({ jobId, source: 'useStructuredImagesByBlade' });
  const videos = isSabre(inspection) ? allVideos.filter((vdo) => vdo.id === videoId) : allVideos;

  const images = isSabre(inspection)
    ? allImages.filter((img) =>
        (videos?.[0]?.attributes?.videoname || '').includes(img.attributes.source_video_filename),
      )
    : allImages;

  const checkForFilterImages = useCallback(
    (image, selectedDamages, selectedCategories, damageAllChecked, categoryAllChecked) => {
      const isDamageType = selectedDamages.includes('clear');
      const isUnreviewedType = selectedDamages.includes('unreviewed');
      const isUncategorizedType = selectedCategories.includes('Uncategorized');
      const isDamageUnreviewedSlected =
        selectedDamages?.includes('clear') && selectedDamages?.includes('unreviewed');
      const damageCategoryMapping = image?.annotations[0]?.annotationDetails?.map(
        ({ damage, severity }) => ({
          damage: damage,
          severity: severity,
        }),
      );
      const imageAttributes = Object.entries(image?.attributes)?.map(([key, value]) => ({
        key: key,
        value: value,
      }));
      const ImageViewed = [];
      imageAttributes?.forEach((value) => {
        if (value?.key === 'viewed') {
          ImageViewed.push({
            key: value?.key,
            value: value?.value,
          });
        }
      });
      const isUnreviewed = checkForDamageReviewed(
        selectedDamages,
        ImageViewed,
        'unreviewed',
        'false',
      );

      if (damageAllChecked && categoryAllChecked) {
        return image;
      } else if (
        selectedDamages.length &&
        selectedCategories.length &&
        !isDamageType &&
        !isUnreviewedType &&
        !categoryAllChecked &&
        !isUncategorizedType
      ) {
        return damageCategoryMapping?.find(
          ({ damage, severity }) =>
            selectedDamages.includes(damage) && selectedCategories.includes(severity),
        );
      } else if (
        selectedDamages.length &&
        selectedCategories.length &&
        !isDamageType &&
        !isUnreviewedType &&
        !categoryAllChecked &&
        isUncategorizedType
      ) {
        return (
          damageCategoryMapping?.find(
            ({ damage, severity }) =>
              selectedDamages.includes(damage) && selectedCategories.includes(severity),
          ) ||
          damageCategoryMapping?.find(
            ({ damage, severity }) =>
              selectedDamages.includes(damage) && checkForUncategorized(severity),
          )
        );
      } else if (
        selectedDamages.length &&
        selectedCategories.length &&
        !isDamageType &&
        !isUnreviewedType &&
        categoryAllChecked
      ) {
        return damageCategoryMapping?.find(({ damage }) => selectedDamages.includes(damage));
      } else if (
        selectedDamages.length &&
        selectedCategories.length &&
        !categoryAllChecked &&
        !isUncategorizedType &&
        (isDamageType || isUnreviewedType)
      ) {
        return (
          damageCategoryMapping?.find(
            ({ damage, severity }) =>
              selectedDamages.includes(damage) && selectedCategories.includes(severity),
          ) ||
          isUnreviewed ||
          (isDamageType &&
            !checkForDamage(image, 'human') &&
            !checkForDamage(image, 'adr') &&
            !checkForDamage(image, 'third party'))
        );
      } else if (
        selectedDamages.length &&
        selectedCategories.length &&
        !categoryAllChecked &&
        isUncategorizedType &&
        (isDamageType || isUnreviewedType)
      ) {
        return (
          damageCategoryMapping?.find(
            ({ damage, severity }) =>
              selectedDamages.includes(damage) && selectedCategories.includes(severity),
          ) ||
          damageCategoryMapping?.find(
            ({ damage, severity }) =>
              selectedDamages.includes(damage) && checkForUncategorized(severity),
          ) ||
          isUnreviewed ||
          (isDamageType &&
            !checkForDamage(image, 'human') &&
            !checkForDamage(image, 'adr') &&
            !checkForDamage(image, 'third party'))
        );
      } else if (
        selectedDamages.length &&
        selectedCategories.length &&
        categoryAllChecked &&
        (isDamageType || isUnreviewedType)
      ) {
        return (
          damageCategoryMapping?.find(({ damage }) => selectedDamages.includes(damage)) ||
          isUnreviewed ||
          (isDamageType &&
            !checkForDamage(image, 'human') &&
            !checkForDamage(image, 'adr') &&
            !checkForDamage(image, 'third party'))
        );
      } else if (selectedDamages.length && isDamageType && !isDamageUnreviewedSlected) {
        return (
          isDamageType &&
          !checkForDamage(image, 'human') &&
          !checkForDamage(image, 'adr') &&
          !checkForDamage(image, 'third party')
        );
      } else if (selectedDamages.length && isUnreviewedType && !isDamageUnreviewedSlected) {
        return isUnreviewed;
      } else if (isDamageType && isUnreviewedType && isDamageUnreviewedSlected) {
        return (
          (isDamageType &&
            !checkForDamage(image, 'human') &&
            !checkForDamage(image, 'adr') &&
            !checkForDamage(image, 'third party')) ||
          isUnreviewed
        );
      }
      return false;
    },
    [],
  );

  const checkForDamage = (image, damage) => {
    let damageAnnotation = image?.annotations[0]?.annotationDetails?.map((img) => img.damage);
    let foundDamage = damageAnnotation?.includes(damage);
    return foundDamage;
  };

  const checkForDamageReviewed = (selectedDamages, ImageViewed, param, flag) => {
    let isViewed = false;
    selectedDamages?.forEach((selectedDamage) => {
      if (selectedDamage === param) {
        Object.values(ImageViewed)?.forEach((value) => {
          if (value?.value === flag) {
            isViewed = true;
          }
        });
      }
    });
    return isViewed;
  };

  const checkForUncategorized = (category) => {
    let isCategorized = false;
    const defaultCategories = [
      'Category 1',
      'Category 2',
      'Category 3',
      'Category 4',
      'Category 5',
      'Indication',
    ];
    const includesAll = (arr, value) => arr?.includes(value);
    isCategorized = !includesAll(defaultCategories, category);
    return isCategorized;
  };

  const getDamagedTools = useCallback((selectedThumbnails) => {
    const {
      includedInReport,
      size,
      category1,
      category2,
      category3,
      category4,
      category5,
      indication,
      Uncategorized,
      damageSelectAll,
      categorySelectAll,
      ...restDamageThumbnails
    } = selectedThumbnails;
    return restDamageThumbnails;
  }, []);

  const getCategoryTools = useCallback((selectedThumbnails) => {
    const {
      adrAnnotation,
      humanAnnotation,
      thirdPartyAnnotation,
      unreviewed,
      clear,
      includedInReport,
      size,
      damageSelectAll,
      categorySelectAll,
      ...restCategoryThumbnails
    } = selectedThumbnails;
    return restCategoryThumbnails;
  }, []);

  const getFilteredImages = useCallback(
    (images) => {
      const getDamageImage = getDamagedTools(selectedTools);
      const getCategoryImage = getCategoryTools(selectedTools);
      const damageAllChecked = Object.values(getDamageImage).every((val) => val === true);
      const categoryAllChecked = Object.values(getCategoryImage).every((val) => val === true);
      const selectedDamages = Object.keys(getDamageImage)
        ?.map((selectedTool) => {
          switch (selectedTool) {
            case 'humanAnnotation':
              if (selectedTools[selectedTool]) {
                return 'human';
              }
              break;
            case 'adrAnnotation':
              if (selectedTools[selectedTool]) {
                return 'adr';
              }
              break;
            case 'thirdPartyAnnotation':
              if (selectedTools[selectedTool]) {
                return 'third party';
              }
              break;
            case 'clear':
              if (selectedTools[selectedTool]) {
                return 'clear';
              }
              break;
            case 'unreviewed':
              if (selectedTools[selectedTool]) {
                return 'unreviewed';
              }
          }
        })
        .filter((notUndefined) => notUndefined !== undefined);
      const selectedCategories = Object.keys(getCategoryImage)
        ?.map((selectedTool) => {
          switch (selectedTool) {
            case 'category1':
              if (selectedTools[selectedTool]) {
                return 'Category 1';
              }
              break;
            case 'category2':
              if (selectedTools[selectedTool]) {
                return 'Category 2';
              }
              break;
            case 'category3':
              if (selectedTools[selectedTool]) {
                return 'Category 3';
              }
              break;
            case 'category4':
              if (selectedTools[selectedTool]) {
                return 'Category 4';
              }
              break;
            case 'category5':
              if (selectedTools[selectedTool]) {
                return 'Category 5';
              }
              break;
            case 'indication':
              if (selectedTools[selectedTool]) {
                return 'Indication';
              }
              break;
            case 'Uncategorized':
              if (selectedTools[selectedTool]) {
                return 'Uncategorized';
              }
          }
        })
        .filter((notUndefined) => notUndefined !== undefined);

      const _filteredImages = images.filter((image) =>
        checkForFilterImages(
          image,
          selectedDamages,
          selectedCategories,
          damageAllChecked,
          categoryAllChecked,
        ),
      );
      return _filteredImages;
    },
    [checkForFilterImages, getCategoryTools, getDamagedTools, selectedTools],
  );

  const filteredImages = useMemo(() => {
    if (inspection !== null && images?.length > 0) {
      if (isSabre(inspection)) {
        // Sabre uses inspection images to generate this structure
        const _filteredImages = getFilteredImages(images);
        return _filteredImages;
      }
      if (isBurst(inspection)) {
        const _filteredImages = getFilteredImages(images);
        return _filteredImages;
      }
      if (isPinwheel(inspection)) {
        const _filteredImages = getFilteredImages(images);
        return _filteredImages;
      }
      if (isInternal(inspection) || isInternalGEBlade(inspection) || isDrone(inspection)) {
        // inspection.videos
        const _filteredImages = getFilteredImages(images);
        return _filteredImages;
      }
    }
    return [];
  }, [inspection, images, getFilteredImages]);

  const structuredImagesByBlade = useMemo(() => {
    if (
      inspection !== null &&
      images?.length > 0 &&
      videos?.length > 0 &&
      filteredImages?.length >= 0
    ) {
      const bladeMap = { ...initialStruct.bladeMap };
      const structuredImages = filteredImages.reduce(
        (accum, curr) => {
          let bladeIndex;
          if (isSabre(inspection)) {
            const bladeValue = curr.attributes.imgname.slice(-5);
            bladeIndex = bladeValue.substring(0, 1); // the last value of imgnme(eg B_01.png) tells about which blade the image belongs to hence taking that as blade index
          } else {
            bladeIndex = curr.attributes.blade_index;
          }
          if (isSabre(inspection)) {
            bladeMap[bladeIndex] = `blade sn ${bladeIndex}`;
          } else {
            const video = videos.find((v) =>
              curr.attributes.imgname.includes(v.attributes.videoname),
            );
            bladeMap[bladeIndex] = String(video?.attributes?.blade_serial_no);
          }
          accum.imagesViewed[bladeIndex] += curr.attributes.viewed === 'true' ? 1 : 0;
          return {
            ...accum,
            [bladeIndex]: (accum?.[bladeIndex] || []).concat(curr),
          };
        },
        { ...initialStruct },
      );

      const _structuredImagesByBlade = {
        ...structuredImages,
        bladeMap,
      };

      return _structuredImagesByBlade;
    }
  }, [inspection, images, videos, filteredImages, initialStruct]);

  return structuredImagesByBlade;
};

export const useDeleteJob = () => {
  const { mutate } = useMutation(async ({ jobId }) =>
    !jobId ? () => {} : await inspectionService.deleteJob(jobId, {}),
  );
  return { mutate };
};

export const useUpdateAnnotation = () => {
  const { mutate } = useMutation((data) => {
    return !data ? () => {} : inspectionService.updateAnnotation(0, data, {});
  });
  return { mutate };
};

export const usePostManualAnnotationImage = () => {
  const { mutate } = useMutation((data) => {
    return !data ? () => {} : inspectionService.postManualAnnotationImage(data, {});
  });
  return { mutate };
};

export const useDeleteAnnotation = () => {
  const { mutate, isLoading } = useMutation(async ({ annotationId }) =>
    !annotationId ? () => {} : await inspectionService.deleteAnnotation(annotationId, {}),
  );
  return { mutate, isLoading };
};

export const usePatchMetadata = () => {
  const { mutate } = useMutation(({ jobId, data, parameter }) =>
    !data || !jobId ? () => {} : inspectionService.patchMetadata(jobId, data, parameter, {}),
  );
  return { mutate };
};

export const usePostJob = () => {
  const { mutate } = useMutation(({ jobId, data }) =>
    !data && !jobId ? () => {} : inspectionService.postJob(jobId, data, {}),
  );
  return { mutate };
};

export const usePatchJob = () => {
  const { mutate } = useMutation(({ jobId, data }) =>
    !data && !jobId ? () => {} : inspectionService.patchJob(jobId, data, {}),
  );
  return { mutate };
};

export const useGetPresignedUrl = () => {
  const { mutate } = useMutation(({ data }) =>
    !data ? () => {} : filesService.getPresignedUrl(data, {}),
  );
  return { mutate };
};

export const usePutFilesByPresignedUrl = () => {
  const { mutate } = useMutation(({ presignedUrl, fileData, uploadProgress }) =>
    !fileData && !presignedUrl
      ? () => {}
      : filesService.putFilesByPresignedUrl(presignedUrl, fileData, {}, uploadProgress),
  );
  return { mutate };
};

export const useDroneSpreadsheetUpload = () => {
  const { mutate } = useMutation(({ formData, headers, uploadProgress }) =>
    // !formData ? () => {} : filesService.droneSpreadsheetUpload(formData, headers, uploadProgress),
    !formData ? () => {} : droneSpreadsheetUpload(formData, headers, uploadProgress),
  );
  return { mutate };
};

export const useCreateJobExecution = () => {
  const { mutate } = useMutation(({ jobId, data }) =>
    !data && !jobId ? () => {} : inspectionService.createJobExecution(jobId, data, {}),
  );
  return { mutate };
};

// export const useUpdateManufacturerByJobId = () => {
//   const { mutate } = useMutation(({ jobId, data }) =>
//     !data && !jobId ? () => {} : filesService.updateManufacturerByJobId(jobId, data, {}),
//   );
//   return { mutate };
// };

export const usePostMetadata = () => {
  const { mutate } = useMutation(({ jobId, data }) =>
    !data && !jobId ? () => {} : inspectionService.postMetadata(jobId, data, {}),
  );
  return { mutate };
};

export const useTurbineDetails_list = () => {
  const { mutate } = useMutation(({ site_id }) =>
    appService.getTurbineDetailForSiteId(site_id, {}),
  );
  return { mutate };
};

export const useChangeJobWorkflow = () => {
  const { mutate } = useMutation(({ jobId, data }) =>
    !data && !jobId ? () => {} : inspectionService.changeJobWorkflow(jobId, data, {}),
  );
  return { mutate };
};

export const useUploadStageInfo = ({ jobId, staleTime = 0 }) => {
  const {
    data = [],
    error,
    isLoading,
    refetch,
  } = useQuery(
    ['useUploadStageInfo', +jobId],
    async () => {
      if (!jobId) return null;
      const { result } = await inspectionService.getUploadStageInfo(jobId, {});
      return { id: jobId, stage: InspectionStages.UPLOAD, data: result };
    },
    {
      ...Config.EXECUTE_ONCE,
      staleTime,
      // refetchInterval: INSPECTION_STAGE_STATUS_REFRESH_INTERVAL,
    },
  );
  return {
    data,
    error,
    isLoading,
    refetch,
  };
};

export const useExecutionStageInfo = ({ jobId, staleTime = 0 }) => {
  const {
    data = [],
    error,
    isLoading,
    refetch,
  } = useQuery(
    ['useExecutionStageInfo', +jobId],
    async () => {
      if (!jobId) return null;
      const { result } = await inspectionService.getExecutionStageInfo(jobId, {});
      return { id: jobId, stage: InspectionStages.EXECUTION, data: result };
    },
    {
      ...Config.EXECUTE_ONCE,
      staleTime,
      // refetchInterval: INSPECTION_STAGE_STATUS_REFRESH_INTERVAL,
    },
  );
  return {
    data,
    error,
    isLoading,
    refetch,
  };
};

export const useStageInfo = ({ stage, jobId, staleTime = 0 }) => {
  const {
    data = {},
    error,
    isLoading,
  } = useQuery(
    ['useStageInfo', stage, +jobId],
    async () => {
      if (!stage || !jobId) return {};
      if (stage === InspectionStages.UPLOAD) {
        const { result } = await inspectionService.getUploadStageInfo(jobId, {});
        return { id: jobId, stage: InspectionStages.UPLOAD, data: result };
      }
      if (stage === InspectionStages.EXECUTION) {
        const { result } = await inspectionService.getExecutionStageInfo(jobId, {});
        return { id: jobId, stage: InspectionStages.EXECUTION, data: result };
      }
      if (stage === InspectionStages.POST_PROCESS) {
        const { result } = await inspectionService.getPostProcessStageInfo(jobId, {});
        return { id: jobId, stage: InspectionStages.POST_PROCESS, data: result };
      }
      if (stage === InspectionStages.REPORT_GENERATION) {
        const { result } = await reportService.getReportStageInfo(jobId, {});
        return { id: jobId, stage: InspectionStages.REPORT_GENERATION, data: result };
      }
    },
    {
      ...Config.EXECUTE_ONCE,
      staleTime,
    },
  );
  return {
    data,
    error,
    isLoading,
  };
};

export const usePostProcessStageInfo = ({ jobId, staleTime = 1000 }) => {
  const [postProcessStageInfo, setPostProcessStageInfo] = useState(null);
  const {
    data = null,
    error,
    isLoading,
  } = useQuery(
    ['usePostProcessStageInfo', +jobId],
    async () => {
      const { result } = await inspectionService.getPostProcessStageInfo(jobId, {});
      return result.data;
    },
    {
      ...Config.EXECUTE_ONCE,
      staleTime,
      // refetchInterval: INSPECTION_STAGE_STATUS_REFRESH_INTERVAL,
    },
  );
  if (data) setPostProcessStageInfo(data);
  return {
    data: postProcessStageInfo,
    error,
    isLoading,
  };
};

export const useReportStageInfo = ({ jobId, staleTime = 0 }) => {
  const {
    data = [],
    error,
    isLoading,
  } = useQuery(
    ['useReportStageInfo'],
    async () => {
      const { result } = await reportService.getReportStageInfo(jobId, {});
      return result.data;
    },
    {
      ...Config.EXECUTE_ONCE,
      staleTime,
      // refetchInterval: INSPECTION_STAGE_STATUS_REFRESH_INTERVAL,
    },
  );
  return {
    data,
    error,
    isLoading,
  };
};

export const useUpdateVideo = () => {
  const queryClient = useQueryClient();
  let payloadData = null;
  const { mutate } = useMutation(
    ({ data }) => {
      payloadData = data;
      return !data ? () => {} : inspectionService.updateVideo(0, data, {});
    },
    {
      onSuccess: () => {
        const jobId = payloadData.jobstable_lid;
        queryClient.setQueryData(['useVideos', +jobId], (oldData) => {
          return oldData.map((d) =>
            d.attributes.videoname === payloadData.videoname
              ? {
                  ...d,
                  attributes: {
                    ...d.attributes,
                    ...payloadData,
                  },
                }
              : d,
          );
        });
      },
    },
  );
  return { mutate };
};

export const useBulkUpdateVideo = () => {
  const queryClient = useQueryClient();
  let payloadData = null;
  const { mutate } = useMutation(
    ({ data }) => {
      payloadData = data;
      return !data
        ? () => {}
        : inspectionService.bulkUpdateVideo(`{"videos":${JSON.stringify(data)}}`, {});
    },
    {
      onSuccess: () => {
        const jobId = payloadData[0].jobstable_lid;
        queryClient.setQueryData(['useVideos', +jobId], (oldData) => {
          oldData.map((d) => {
            payloadData.map(
              (p) =>
                d.attributes.videoname === p.videoname ?? {
                  ...d,
                  attributes: {
                    ...d.attributes,
                    ...p,
                  },
                },
            );
          });
        });
      },
    },
  );
  return { mutate };
};

export const useUpdateImage = () => {
  const queryClient = useQueryClient();
  let payloadData = null;
  const { mutate } = useMutation(
    ({ flagNew, data }) => {
      payloadData = data;
      return !data ? () => {} : inspectionService.updateImage(flagNew, data, {});
    },
    {
      onSuccess: (results) => {
        const imageId = results.id;
        queryClient.setQueryData(['useImage', +imageId], (oldData) => {
          return {
            ...oldData,
            attributes: {
              ...oldData.attributes,
              ...payloadData,
            },
          };
        });
      },
    },
  );
  return { mutate };
};
